import React, { useState, useEffect } from "react";

import {
  DataGridPro,
  gridClasses,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiRef,
} from "@mui/x-data-grid-pro"; // Changed to Pro package
import Box from "@mui/material/Box";
import {
  Tabs,
  Tab,
  styled,
  alpha,
  Radio,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Tooltip,
  Button,
} from "@mui/material";
import { useAuth } from "./AuthContext";
import {
  hitter_columns,
  hitter_columns_mobile,
  pitcher_columns,
  pitcher_columns_mobile,
} from "./utils/ColumnDefs";

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGridPro)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      "&:hover": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
          theme.palette.action.selectedOpacity +
          theme.palette.action.hoverOpacity
        ),
        // Reset on touch devices, it doesn't add specificity
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
  [`& .${gridClasses.row}.odd`]: {
    backgroundColor: "white",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      "&:hover": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
          theme.palette.action.selectedOpacity +
          theme.palette.action.hoverOpacity
        ),
        // Reset on touch devices, it doesn't add specificity
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
}))

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}




export default function Leaderboard(props: any) {
  const apiRef = useGridApiRef();
  const { user } = useAuth();

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const [hitterData, setHitterData] = useState([]);
  const [pitcherData, setPitcherData] = useState([]);

  const h_columns = props.isDesktop
    ? hitter_columns
    : hitter_columns_mobile;
  const p_columns = props.isDesktop
    ? pitcher_columns
    : pitcher_columns_mobile;

  const [level, setLevel] = React.useState("AAA");
  const [season, setSeason] = React.useState("2026");



  const handleLevel = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLevel(event.target.value);
  };
  //console.log(level);

  const handleSeason = (event: SelectChangeEvent) => {
    setSeason(event.target.value as string);
  };
  //console.log(season);
  const [qualifier, setQualifier] = React.useState("100");

  const handleQualifier = (event: SelectChangeEvent) => {
    setQualifier(event.target.value as string);
  };

  const [organization, setOrganization] = React.useState("All");

  const handleOrganization = (event: SelectChangeEvent) => {
    setOrganization(event.target.value as string);
  };


  const [ageRange, setAgeRange] = React.useState<number[]>([16, 28]);

  const handleAgeRange = (event: Event, newValue: any) => {
    setAgeRange(newValue);
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ backgroundColor: "#FFFFFF" }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        {(user?.is_plus || /^(localhost|127\.0\.0\.1|192\.168\.|10\.)/.test(window.location.hostname)) ? (
          <GridToolbarExport />
        ) : (
          <Tooltip title="PS Plus required to export">
            <span>
              <Button
                size="small"
                href="/plus"
                sx={{ fontSize: "0.8125rem", textTransform: "none", color: "rgba(0,0,0,0.38)" }}
              >
                Export 🔒
              </Button>
            </span>
          </Tooltip>
        )}
        {/* Added Quick Filter */}
        <GridToolbarQuickFilter
          quickFilterParser={(searchInput) =>
            searchInput.split(',').map((value) => value.trim())
          }
          sx={{ marginLeft: 'auto', width: props.isDesktop ? 500 : 250 }}
        />
        {level === "A" && (
          <div>
            <span style={{ fontWeight: "bold", color: "#DC707A" }}>
              ** Only 10 Organizations (those of the Florida State League) Have Single A Data Available **
            </span>
          </div>
        )}
      </GridToolbarContainer>
    );
  }


  useEffect(() => {
    //console.log("Data: ", level);

    fetch(props.prod ? "https://oriolebird.pythonanywhere.com/leaders/hitters/" + level + "/" + season + "/" + qualifier + "/" + ageRange[0] + "/" + ageRange[1] : "/leaders/hitters/" + level + "/" + season + "/" + qualifier + "/" + ageRange[0] + "/" + ageRange[1])
      .then((res) => res.json())
      .then((data) => {
        //console.log(data);
        setHitterData(data.data);
        //console.log("HDATA", data.data);
      });
    fetch(props.prod ? "https://oriolebird.pythonanywhere.com/leaders/pitchers/" + level + "/" + season + "/" + qualifier + "/" + ageRange[0] + "/" + ageRange[1] : "/leaders/pitchers/" + level + "/" + season + "/" + qualifier + "/" + ageRange[0] + "/" + ageRange[1])
      .then((res) => res.json())
      .then((data) => {
        //console.log(data);
        setPitcherData(data.data);
        //console.log("PDATA", data.data);
      });
  }, [level, props.prod, season, qualifier, ageRange]);

  return (
    <div style={{ width: "100%" }}>
      <title>Prospect Savant</title>
      <meta
        name="description"
        content="Prospect Savant - Minor League Baseball Savant"
      />
      <meta
        name="title"
        content="Prospect Savant"
      />
      <meta property="og:description" content="Prospect Savant - Minor League Baseball Savant" />
      <meta property="og:title" content="Prospect Savant" />
      <Box sx={{ width: "100%", marginTop: "-15px" }}>
        <Box
          sx={{ borderBottom: 1, borderColor: "divider" }}
          paddingLeft={"50px"}
          flexDirection="row"
        >
          <Tabs value={value} onChange={handleChange}>
            <Tab label="Hitters" {...a11yProps(0)} />
            <Tab label="Pitchers" {...a11yProps(1)} />
          </Tabs>
        </Box>
        <div
          style={{
            marginTop: "10px",
            marginBottom: "-20px",
            marginLeft: "30px",
            verticalAlign: "baseline",
          }}
        >
          <FormControl>
            <FormLabel id="demo-row-radio-buttons-group-label">Level</FormLabel>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
            >
              <FormControlLabel
                value="A"
                control={
                  <Radio
                    checked={level === "A"}
                    onChange={handleLevel}
                    value="A"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "A" }}
                  />
                }
                label="A"
              />
              <FormControlLabel
                value="AAA"
                control={
                  <Radio
                    checked={level === "AAA"}
                    onChange={handleLevel}
                    value="AAA"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "AAA" }}
                  />
                }
                label="AAA"
              />
            </RadioGroup>
          </FormControl>
          <FormControl>
            <InputLabel id="demo-simple-select-label">Season</InputLabel>
            <Select
              value={season}
              label="Season"
              onChange={handleSeason}
              defaultValue="2026"
            >
              <MenuItem value={2026}>2026</MenuItem>
              <MenuItem value={2025}>2025</MenuItem>
              <MenuItem value={2024}>2024</MenuItem>
              <MenuItem value={2023}>2023</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel id="demo-simple-select-label">Pitch Qualifier</InputLabel>
            <Select
              value={qualifier}
              label="Pitch Qualifier"
              onChange={handleQualifier}
              defaultValue="100"
              sx={{ width: "102px" }}
            >
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={150}>150</MenuItem>
              <MenuItem value={200}>200</MenuItem>
              <MenuItem value={250}>250</MenuItem>
              <MenuItem value={300}>300</MenuItem>
              <MenuItem value={400}>400</MenuItem>
              <MenuItem value={500}>500</MenuItem>
              <MenuItem value={600}>600</MenuItem>
              <MenuItem value={700}>700</MenuItem>
              <MenuItem value={800}>800</MenuItem>
              <MenuItem value={900}>900</MenuItem>
              <MenuItem value={1000}>1000</MenuItem>
              <MenuItem value={1250}>1250</MenuItem>
              <MenuItem value={1500}>1500</MenuItem>
            </Select>
          </FormControl>
          {!props.isDesktop && <br></br>}
          <span style={{ position: "absolute", marginLeft: props.isDesktop ? "25px" : "0px", marginTop: "3px", color: "#5A6465", fontSize: 16 }}>Age Range</span>
          <Slider
            getAriaLabel={() => 'Age Range'}
            value={ageRange}
            onChange={handleAgeRange}
            valueLabelDisplay="on"
            min={16}
            max={40}
            disableSwap={true}
            sx={{
              width: "200px", marginLeft: props.isDesktop ? "30px" : "10px", marginTop: "25px",
              '& .MuiSlider-valueLabel': {
                fontSize: 11,
                fontWeight: 'bold',
                marginTop: "33px",
                backgroundColor: 'unset',
                color: '#FFFFFF',
                '&::before': {
                  display: 'none',
                },
                '& *': {
                  background: 'transparent',
                  color: '#FFFFFF',
                },
              },
            }}
          />
          <FormControl style={{ marginLeft: "10px" }}>
            <InputLabel id="demo-simple-select-label">Organization</InputLabel>
            <Select
              value={organization}
              label="Organization"
              onChange={handleOrganization}
              defaultValue="All"
              sx={{ width: "100px", }}
            >
              <MenuItem value={"All"}>All</MenuItem>
              <MenuItem value={"ARI"}>ARI</MenuItem>
              <MenuItem value={"ATL"}>ATL</MenuItem>
              <MenuItem value={"ATH"}>ATH</MenuItem>
              <MenuItem value={"BAL"}>BAL</MenuItem>
              <MenuItem value={"BOS"}>BOS</MenuItem>
              <MenuItem value={"CHC"}>CHC</MenuItem>
              <MenuItem value={"CHW"}>CHW</MenuItem>
              <MenuItem value={"CIN"}>CIN</MenuItem>
              <MenuItem value={"CLE"}>CLE</MenuItem>
              <MenuItem value={"COL"}>COL</MenuItem>
              <MenuItem value={"DET"}>DET</MenuItem>
              <MenuItem value={"HOU"}>HOU</MenuItem>
              <MenuItem value={"KCR"}>KCR</MenuItem>
              <MenuItem value={"LAA"}>LAA</MenuItem>
              <MenuItem value={"LAD"}>LAD</MenuItem>
              <MenuItem value={"MIA"}>MIA</MenuItem>
              <MenuItem value={"MIL"}>MIL</MenuItem>
              <MenuItem value={"MIN"}>MIN</MenuItem>
              <MenuItem value={"NYM"}>NYM</MenuItem>
              <MenuItem value={"NYY"}>NYY</MenuItem>
              <MenuItem value={"PHI"}>PHI</MenuItem>
              <MenuItem value={"PIT"}>PIT</MenuItem>
              <MenuItem value={"SDP"}>SDP</MenuItem>
              <MenuItem value={"SEA"}>SEA</MenuItem>
              <MenuItem value={"SFG"}>SFG</MenuItem>
              <MenuItem value={"STL"}>STL</MenuItem>
              <MenuItem value={"TBR"}>TBR</MenuItem>
              <MenuItem value={"TEX"}>TEX</MenuItem>
              <MenuItem value={"TOR"}>TOR</MenuItem>
              <MenuItem value={"WSN"}>WSN</MenuItem>
            </Select>
          </FormControl>
        </div>
        <CustomTabPanel value={value} index={0}>
          <StripedDataGrid
            apiRef={apiRef} // Added for Pro API access
            rows={organization === "All" ? hitterData : hitterData.filter((row: any) => row.MLB_AbbName === organization)}
            sx={{
              height: "80vh",
              width: props.isDesktop ? "100%" : "110%",
              marginLeft: props.isDesktop ? "0px" : "-20px",
            }}
            columns={h_columns}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            pageSizeOptions={[25, 50, 100]}

            disableColumnResize={false} // Enable column resizing

            slots={{
              toolbar: CustomToolbar,
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: "score_p", sort: "desc" }],
              },
              density: "compact",
              pinnedColumns: {
                left: ['name'], // Pin player name by default
              }, // Initial pinned columns
            }}
          />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <StripedDataGrid
            apiRef={apiRef} // Added for Pro API access
            sx={{
              height: "80vh",
              width: props.isDesktop ? "100%" : "110%",
              marginLeft: props.isDesktop ? "0px" : "-20px",
            }}
            rows={organization === "All" ? pitcherData : pitcherData.filter((row: any) => row.MLB_AbbName === organization)}
            columns={p_columns}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            pagination
            pageSizeOptions={[25, 50, 100]}

            disableColumnResize={false} // Enable column resizing

            slots={{
              toolbar: CustomToolbar,
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: "score_p", sort: "desc" }],
              },
              density: "compact",
              pinnedColumns: {
                left: ['name'], // Pin player name by default
              }, // Initial pinned columns
            }}
          />
        </CustomTabPanel>
      </Box>
    </div>
  );
}
